import type { CSSProperties } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, useReducedMotion } from 'framer-motion'
import { useAuth } from '../auth/AuthContext'
import { hapticLight } from '../auth/telegram'
import { EASE_OUT } from '../../../shared/motion.ts'
import brandMark from '../../../shared/assets/snowfold-atlas-mark.png'
import crashArt from '../../../shared/assets/crash-entry.webp'
import hiloArt from '../../../shared/assets/hilo-entry.webp'
import minesArt from '../../../shared/assets/mines-world.webp'
import diceArt from '../../../shared/assets/dice-world.webp'
import plinkoArt from '../../../shared/assets/plinko-world.webp'
import blastArt from '../../../shared/assets/blast-entry.webp'

// Local defaults follow docs/ports.md; production must set VITE_*_URL explicitly.
const CRASH_URL =
  (import.meta.env.VITE_CRASH_URL as string | undefined) ||
  'http://127.0.0.1:18811'
const HILO_URL =
  (import.meta.env.VITE_HILO_URL as string | undefined) ||
  'http://127.0.0.1:18812'
const MINES_URL =
  (import.meta.env.VITE_MINES_URL as string | undefined) ||
  'http://127.0.0.1:18813'
const DICE_URL =
  (import.meta.env.VITE_DICE_URL as string | undefined) ||
  'http://127.0.0.1:18814'
const PLINKO_URL =
  (import.meta.env.VITE_PLINKO_URL as string | undefined) ||
  'http://127.0.0.1:18815'
const BLAST_URL =
  (import.meta.env.VITE_BLAST_URL as string | undefined) ||
  'http://127.0.0.1:18816'

type GameCard = {
  id: string
  displayName: string
  nameKey: string
  descKey: string
  url: string
  accent: string
  art: string
  featured?: boolean
}

export function Home() {
  const { t } = useTranslation()
  const reduceMotion = useReducedMotion()
  const { token } = useAuth()

  const games: GameCard[] = [
    {
      id: 'crash',
      displayName: 'CRASH',
      nameKey: 'home.crashName',
      descKey: 'home.crashDesc',
      url: CRASH_URL,
      accent: '#ff6b50',
      art: crashArt,
      featured: true,
    },
    {
      id: 'hilo',
      displayName: 'HI-LO',
      nameKey: 'home.hiloName',
      descKey: 'home.hiloDesc',
      url: HILO_URL,
      accent: '#39dbc7',
      art: hiloArt,
    },
    {
      id: 'mines',
      displayName: 'MINES',
      nameKey: 'home.minesName',
      descKey: 'home.minesDesc',
      url: MINES_URL,
      accent: '#f4bd4f',
      art: minesArt,
    },
    {
      id: 'dice',
      displayName: 'DICE',
      nameKey: 'home.diceName',
      descKey: 'home.diceDesc',
      url: DICE_URL,
      accent: '#d56cff',
      art: diceArt,
    },
    {
      id: 'plinko',
      displayName: 'PLINKO',
      nameKey: 'home.plinkoName',
      descKey: 'home.plinkoDesc',
      url: PLINKO_URL,
      accent: '#43e6ad',
      art: plinkoArt,
    },
    {
      id: 'blast',
      displayName: 'BLAST ROOMS',
      nameKey: 'home.blastName',
      descKey: 'home.blastDesc',
      url: BLAST_URL,
      accent: '#ff8a4c',
      art: blastArt,
    },
  ]

  // Cross-origin Mini Apps lose Telegram initData; hand off JWT + lobby URL.
  const openGame = (url: string, nameKey: string) => {
    if (!url) {
      alert(t('home.urlMissing', { name: t(nameKey) }))
      return
    }
    hapticLight()
    try {
      const u = new URL(url, window.location.href)
      if (token) u.searchParams.set('token', token)
      u.searchParams.set('lobby', `${window.location.origin}/`)
      window.location.href = u.toString()
    } catch {
      const sep = url.includes('?') ? '&' : '?'
      const lobby = encodeURIComponent(`${window.location.origin}/`)
      window.location.href = token
        ? `${url}${sep}token=${encodeURIComponent(token)}&lobby=${lobby}`
        : `${url}${sep}lobby=${lobby}`
    }
  }

  return (
    <div className="page page-games">
      <h3 className="section-title section-title--games">
        <img src={brandMark} alt="" />
        {t('home.games')}
      </h3>
      <div className="game-grid">
        {games.map((g, i) => (
          <motion.button
            key={g.id}
            type="button"
            className={`game-card game-card--${g.id}${g.featured ? ' game-card--featured' : ''}`}
            style={{
              ['--game-accent' as string]: g.accent,
              ['--card-art' as string]: `url(${g.art})`,
            } as CSSProperties}
            initial={reduceMotion ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={reduceMotion
              ? { duration: 0 }
              : {
                  delay: 0.05 + i * 0.04,
                  duration: 0.24,
                  ease: EASE_OUT,
                }}
            onClick={() => openGame(g.url, g.nameKey)}
          >
            <img
              className="game-art"
              src={g.art}
              alt=""
              width={1536}
              height={1024}
              loading={g.featured ? 'eager' : 'lazy'}
              decoding="async"
              fetchPriority={g.featured ? 'high' : 'auto'}
            />
            <span className="game-shade" aria-hidden />
            <span className="game-meta">
              {g.featured ? (
                <span className="game-eyebrow">{t('home.featured')}</span>
              ) : null}
              <span className="game-title">{g.displayName}</span>
              <span className="game-desc">{t(g.descKey)}</span>
              <span className="game-cta">{t('home.openGame')}</span>
            </span>
            <img className="game-brand-stamp" src={brandMark} alt="" />
          </motion.button>
        ))}
      </div>
    </div>
  )
}
